import APIContext from ".";
import {
  usePatientQuery,
  useDispositionsQuery,
  usePatientDispositionsQuery,
  useEDDQuery,
  useConfigurationQuery,
  useIntelligenceTextQuery,
  usePatientMilestonesQuery,
  usePatientClinicalInsightsQuery,
  useMilestonesQuery,
  useMedicallyReadyStatusQuery,
  usePatientInsightsQuery,
  useActiveRoundingSessionQuery,
  usePatientConsiderationsQuery,
  usePatientNotesQuery,
  useUserPreferencesQuery,
  useQCardReviewStatusQuery,
} from "../../api/queries";
import { postQCardOpen } from "../../api/endpoints";

import type { FC } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { computeProductionDataReadOnly } from "../../utils/patient-embed-environment";
import { AnalyticsBrowser } from "@segment/analytics-next";
import Configuration from "../../types/Configuration";
import { QCardReviewStatusRequestParams } from "../../types/QCardReviewStatus";
import { getClientTimezone } from "../../utils/physician-review-timezone";

const buildQCardOpenKey = ({
  emrHospitalID,
  emrPatientID,
  departmentName,
  timezone,
  qvHospitalID,
}: QCardReviewStatusRequestParams): string =>
  `${emrHospitalID}:${emrPatientID}:${departmentName}:${timezone}:${qvHospitalID ?? ""}`;

interface APIContextProviderProps {
  emrPatientID: string;
  emrHospitalID: string;
  fhirPatientID: string;
  authContextID: string;
  /** Launch/list department name; patient current_department takes precedence when loaded. */
  departmentName?: string | null;
}

const APIContextProvider: FC<APIContextProviderProps> = ({
  children,
  emrPatientID,
  emrHospitalID,
  fhirPatientID,
  authContextID,
  departmentName: launchDepartmentName,
}) => {
  const [analytics, setAnalytics] = useState<AnalyticsBrowser | null>(null);
  const [showErrorToast, setShowErrorToast] = useState<boolean>(false);
  const [sessionExpired, setSessionExpired] = useState<boolean>(false);
  const patientQuery = usePatientQuery(
    emrPatientID,
    emrHospitalID,
    fhirPatientID,
  );

  const configurationQuery = useConfigurationQuery();
  const userPreferencesQuery = useUserPreferencesQuery();

  const configuration: Configuration = configurationQuery?.data
    ? configurationQuery?.data?.[0]
    : null;

  const includeHistory = configuration?.update_history_enabled;

  const patientID = patientQuery.data?.id;
  const isViewOnly = patientQuery.data?.is_view_only ?? false;
  const productionDataReadOnly = useMemo(
    () => computeProductionDataReadOnly(userPreferencesQuery),
    [userPreferencesQuery],
  );
  const patientDataReadOnly = isViewOnly || productionDataReadOnly;

  const patientDispositionsQuery = usePatientDispositionsQuery(
    patientID,
    includeHistory,
  );
  const eddQuery = useEDDQuery(patientID, includeHistory);
  const patientMilestonesQuery = usePatientMilestonesQuery(patientID);
  const patientClinicalInsightsQuery =
    usePatientClinicalInsightsQuery(patientID);
  const intelligenceTextQuery = useIntelligenceTextQuery(patientID);
  const medicallyReadyStatusQuery = useMedicallyReadyStatusQuery(patientID);
  const patientInsightsQuery = usePatientInsightsQuery(patientID);
  const activeRoundingSessionQuery = useActiveRoundingSessionQuery();
  const patientConsiderationsQuery = usePatientConsiderationsQuery(patientID);
  const patientNotesQuery = usePatientNotesQuery(patientID);

  const urlDepartmentName =
    launchDepartmentName ??
    new URLSearchParams(window.location.search).get("emr_dept_name") ??
    undefined;
  const reviewDepartmentName =
    patientQuery.data?.current_department ?? urlDepartmentName ?? undefined;
  const reviewQvHospitalID = patientQuery.data?.qv_hospital_id;
  const reviewTimezone = getClientTimezone(configuration?.physician_review_timezone);
  const asyncPhysicianReviewEnabled = Boolean(
    configuration?.async_physician_review_enabled,
  );
  const physicianReviewPollingEnabled = Boolean(
    emrPatientID &&
      emrHospitalID &&
      patientQuery.isSuccess &&
      reviewDepartmentName &&
      configurationQuery.isSuccess &&
      asyncPhysicianReviewEnabled,
  );

  const qCardReviewStatusParams =
    physicianReviewPollingEnabled && reviewDepartmentName
      ? {
          emrPatientID,
          emrHospitalID,
          departmentName: reviewDepartmentName,
          timezone: reviewTimezone,
          qvHospitalID: reviewQvHospitalID,
        }
      : undefined;

  const qCardReviewStatusQuery = useQCardReviewStatusQuery(qCardReviewStatusParams);

  const queryClient = useQueryClient();
  const qCardOpenRecordedRef = useRef<string | null>(null);

  const { mutate: recordQCardOpen } = useMutation(
    (params: QCardReviewStatusRequestParams) => postQCardOpen(params),
    {
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 8000),
      onSuccess: (_data, variables) => {
        qCardOpenRecordedRef.current = buildQCardOpenKey(variables);
        if (!asyncPhysicianReviewEnabled) {
          return;
        }
        queryClient.invalidateQueries([
          "qcardreviewstatus",
          variables.emrPatientID,
          variables.emrHospitalID,
          variables.departmentName,
          variables.timezone,
          variables.qvHospitalID,
        ]);
      },
    },
  );

  const dispositionsQuery = useDispositionsQuery(patientQuery.isSuccess);
  const milestonesQuery = useMilestonesQuery(patientQuery.isSuccess);

  // Only record opens when async physician review is enabled (same gate as GET polling
  // and the banner). POST is not a shadow-metrics path for flag-off hospitals.
  useEffect(() => {
    if (!physicianReviewPollingEnabled || !reviewDepartmentName) {
      return;
    }
    const openKey = buildQCardOpenKey({
      emrPatientID,
      emrHospitalID,
      departmentName: reviewDepartmentName,
      timezone: reviewTimezone,
      qvHospitalID: reviewQvHospitalID,
    });
    if (qCardOpenRecordedRef.current === openKey) {
      return;
    }
    recordQCardOpen({
      emrPatientID,
      emrHospitalID,
      departmentName: reviewDepartmentName,
      timezone: reviewTimezone,
      qvHospitalID: reviewQvHospitalID,
    });
  }, [
    emrPatientID,
    emrHospitalID,
    reviewDepartmentName,
    reviewTimezone,
    reviewQvHospitalID,
    physicianReviewPollingEnabled,
    recordQCardOpen,
  ]);

  useEffect(() => {
    if (!configurationQuery.isSuccess) return;
    const appSettings = configurationQuery.data?.[0]?.app_settings;
    if (!appSettings?.fe_segment_write_key) return;
    const analyticsInit = AnalyticsBrowser.load({
      writeKey: appSettings.fe_segment_write_key,
    });
    const urlParams = new URLSearchParams(window.location.search);
    analyticsInit.identify(urlParams.get("emr_user_id"), {
      username: urlParams.get("emr_user_full_name"),
      email: urlParams.get("emr_user_email"),
      role: urlParams.get("emr_user_role"),
      service_area: urlParams.get("emr_user_service_area"),
      dept_speciality: urlParams.get("emr_user_dept_speciality"),
      stack: window.location.host.split(".")[0],
    });
    setAnalytics(analyticsInit);
  }, [configurationQuery.isSuccess, configurationQuery.data]);

  // Since events from EBA can fire anytime and update many fields, we use the patient dispositions query to gate the refresh
  // this will cascade across and update everything as needed.
  // Skip the first poll so freshly-seeded cache (from the patient list) isn't immediately invalidated.
  const isFirstPoll = useRef(true);
  useEffect(
    () => {
      if (
        patientDispositionsQuery.isSuccess &&
        !patientDispositionsQuery.isFetching
      ) {
        if (isFirstPoll.current) {
          isFirstPoll.current = false;
          return;
        }
        eddQuery.refetch();
        dispositionsQuery.refetch();
        patientMilestonesQuery.refetch();
        patientClinicalInsightsQuery.refetch();
        intelligenceTextQuery.refetch();
        medicallyReadyStatusQuery.refetch();
        patientConsiderationsQuery.refetch();
      }
    },
    // eslint-disable-next-line
    [patientDispositionsQuery.isFetching, patientDispositionsQuery.isSuccess],
  );

  useEffect(() => {
    // Check for JWT token expired error in patientDispositionsQuery
    if (
      patientDispositionsQuery.error &&
      (patientDispositionsQuery.error as any)?.response?.status === 403 &&
      (patientDispositionsQuery.error as any)?.response?.data?.detail?.includes(
        "JWT token has expired",
      )
    ) {
      setSessionExpired(true);
    }
  }, [patientDispositionsQuery.error]);

  return (
    <APIContext.Provider
      value={{
        patientID,
        patientQuery,
        dispositionsQuery,
        patientDispositionsQuery,
        eddQuery,
        milestonesQuery,
        patientMilestonesQuery,
        patientClinicalInsightsQuery,
        configurationQuery,
        userPreferencesQuery,
        authContextID,
        intelligenceTextQuery,
        medicallyReadyStatusQuery,
        patientInsightsQuery,
        activeRoundingSessionQuery,
        patientConsiderationsQuery,
        patientNotesQuery,
        qCardReviewStatusQuery,
        analytics,
        showErrorToast,
        setShowErrorToast,
        sessionExpired,
        isViewOnly,
        productionDataReadOnly,
        patientDataReadOnly,
      }}
    >
      {children}
    </APIContext.Provider>
  );
};

export default APIContextProvider;
